import { type Optional } from '@databases/types';
import {
  type AvailableContentStates,
  type ContentState,
  ContentStatus
} from '@databases/types/confluence';

import { fetchConfluenceData, rejectOnError } from './fetch';

/**
 * Returns the existing content states that can be assigned to a page. These include "suggested statuses" configured in the space
 * settings and the last 3 custom statuses that the current user has assigned to other pages in the Confluence instance.
 * The result also indicates whether creating custom statuses is allowed by the "customContentStates" property being defined or not.
 */
export async function getAvailableContentStates(
  contentId: string
): Promise<AvailableContentStates> {
  try {
    const res = await fetchConfluenceData(
      `/rest/api/content/${encodeURIComponent(contentId)}/state/available`
    ).then(rejectOnError);
    return await res.json();
  } catch (err: any) {
    if (
      err._orderlyStatus === 403 &&
      err.message.includes('User does not have Page edit permission')
    ) {
      // User is not allowed to edit the page and thus does not receive any content state suggestions.
      // We ignore the error and return an empty object, just like when content states are disabled.
      return {};
    } else {
      throw err;
    }
  }
}

/**
 * Creates a new revision for the given page and sets the given content state on it. If the given content state is a new one
 * that does not have an ID, a custom state is created for it (if permitted). If a custom state or space state already exists
 * with the same name (case sensitive) and colour (case insensitive), that state is reused rather than creating a new one. If
 * no custom state with the same name and colour exists, a new one is created, even if this means that there will be multiple
 * states with the same name (but different colours).
 */
export async function addContentState(
  contentId: string,
  state: Partial<ContentState>,
  status: ContentStatus = ContentStatus.CURRENT
): Promise<void> {
  await fetchConfluenceData(
    `/rest/api/content/${encodeURIComponent(contentId)}/state?${new URLSearchParams({
      status
    })}`,
    {
      method: 'PUT',
      headers: {
        'Content-type': 'application/json'
      },
      body: JSON.stringify(state)
    }
  ).then(rejectOnError);
}

/**
 * Creates a new revision of the given page and removes its content state. If the content state was a custom one, it seems to
 * remain in the list of available content states, even if it is not used on any pages anymore. There seems to be no way to
 * remove custom states.
 */
export async function removeContentState(
  contentId: string,
  status: ContentStatus = ContentStatus.CURRENT
): Promise<void> {
  await fetchConfluenceData(
    `/rest/api/content/${encodeURIComponent(contentId)}/state?${new URLSearchParams({
      status
    })}`,
    {
      method: 'DELETE'
    }
  ).then(rejectOnError);
}

/**
 * Returns a string that can be used as a unique identifier for a content state in a map.
 */
export function getContentStateKey(
  status: Optional<ContentState, 'id'> | null | undefined
): string {
  if (status) {
    return `${status.name}-${status.color.toLowerCase()}`;
  } else if (status === null) {
    return 'null';
  } else {
    return '';
  }
}

/**
 * Returns the given array of content states with duplicates removed.
 */
export function uniqueContentStates<T extends Optional<ContentState, 'id'>>(
  states: T[]
): T[] {
  return Object.values(Object.fromEntries(states.map((s) => [getContentStateKey(s), s])));
}
